import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, CalendarCheck, ChevronRight, CircleUserRound, Dna, FileText, FlaskConical, Gamepad2, Home, LineChart, LogIn,
  Pause, Play, RotateCcw, ShoppingBag, ShoppingCart, Sparkles, Smartphone, Soup, UserRound, Watch, HeartPulse, ListChecks,
} from 'lucide-react';

import { Empty } from './parts';
import { tx } from './i18n';
import {
  AppPhone, TapLayer, momentOf, replayVisits, screenHints, screenName, sessionVisits, stepLabel, useAppStrings, visitTaps,
} from './AppPhone';
import { AppScreen } from './AppScreens';
import { Flag, PlatformMark, counted, initials, lengthOf, versionLabel } from './MobileParts';

const SCREEN_ICONS = {
  launch: Smartphone, signin: LogIn, home: Home, insights: LineChart, reports: FileText, clinical: FlaskConical, ai: Sparkles,
  store: ShoppingBag, checkout: ShoppingCart, orders: ShoppingBag, profile: UserRound, tests: FlaskConical, metric: HeartPulse,
  habits: ListChecks, activity: Activity, diet: Soup, booking: CalendarCheck, games: Gamepad2, device: Watch, omics: Dna, results: Dna,
};
const SPEEDS = [1, 2, 4];

export function ScreenIcon({ screen }) {
  const Icon = SCREEN_ICONS[screen] || Smartphone;
  return <Icon aria-hidden="true" />;
}

// The wall clock where the session happened; the viewer's own when the zone is unknown.
function localTime(at, zone, options) {
  try {
    return new Date(at).toLocaleString(undefined, { ...options, timeZone: zone || undefined });
  } catch {
    return new Date(at).toLocaleString(undefined, options);
  }
}

function dayHeading(at, now) {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (at >= start.getTime()) return tx('ui.mobile.today');
  if (at >= start.getTime() - 86400000) return tx('ui.mobile.yesterday');
  return new Date(at).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

// A new page slides in before anything is pressed on it, whatever the speed;
// presses follow each other with a short pause; the press that leaves a page
// changes it right away; a page the person only went back through shows for
// a moment.
const SETTLE_MS = 300;
const BETWEEN_MS = 160;
const LEAVE_MS = 40;
const RETURN_MS = 360;
const MOMENT_MS = 420;
// A press that never reports back (its page gone mid-press) does not stall the replay.
const PRESS_LIMIT_MS = 4000;

// How long a page stays on the phone at least, presses included: longer visits
// linger a little longer, but nothing holds the replay up for more than a
// couple of seconds besides the presses made on it.
function readingTime(visit, next, speed) {
  if (visit.returned) return RETURN_MS / speed;
  const seconds = Math.max(0, ((next ? next.start : visit.end) - visit.start) / 1000);
  return Math.min(2300, Math.max(550, 450 + Math.log2(1 + seconds) * 220)) / speed;
}

function personOf(session) {
  const name = session.name || session.email || tx('ui.mobile.deleted_account');
  return { name, email: session.email || '', initials: initials(session.name, session.email) };
}

function SessionRow({ session, active, onPick, showPerson }) {
  const visits = useMemo(() => sessionVisits(session).filter((visit) => visit.screen !== 'launch'), [session]);
  return (
    <button type="button" className={`xo-mob-session-row${active ? ' is-active' : ''}`} onClick={() => onPick(session.id)}>
      <span className="xo-mob-session-main">
        {showPerson && <strong>{session.name || session.email || tx('ui.mobile.deleted_account')}</strong>}
        <small>
          <PlatformMark platform={session.platform} />
          {localTime(session.start, session.zone, { hour: '2-digit', minute: '2-digit' })}
          <i aria-hidden="true">·</i>{lengthOf(session.end - session.start)}
          <i aria-hidden="true">·</i>{counted('ui.mobile.screens_count', visits.length)}
        </small>
      </span>
      <span className="xo-mob-session-strip" aria-hidden="true">
        {visits.slice(0, 7).map((visit, index) => <ScreenIcon key={index} screen={visit.screen} />)}
      </span>
    </button>
  );
}

// Sessions on the left, the one picked playing on the phone on the right, and
// the screens it went through beside it.
export default function SessionPlayer({ sessions, initialId = null, showPerson = true, now = Date.now() }) {
  const as = useAppStrings();
  const [pickedId, setPickedId] = useState(() => (sessions.some((session) => session.id === initialId) ? initialId : sessions[0] ? sessions[0].id : null));
  const session = sessions.find((entry) => entry.id === pickedId) || sessions[0] || null;
  // The page's data refreshes while it is open; the same session read again
  // must not restart the replay, so the visits follow what the session holds.
  const latest = useRef(session);
  latest.current = session;
  const held = session ? `${session.id}:${session.steps.length}:${session.end}` : '';
  const visits = useMemo(() => (held && latest.current ? replayVisits(latest.current) : []), [held]);
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [tap, setTap] = useState(null);
  const presses = useRef(0);
  const pending = useRef(new Map());
  const listRef = useRef(null);

  // Picking another session starts it from its first screen in the same
  // render, so the phone never shows the old place in the new session.
  const pick = useCallback((id) => {
    setPickedId(id);
    setIndex(0);
    setPhase(0);
    setPlaying(true);
    setTap(null);
  }, []);

  const visit = visits[Math.min(index, Math.max(0, visits.length - 1))];
  const moment = useMemo(() => {
    if (!visit) return null;
    const step = visit.steps.find((entry) => momentOf(entry) !== null);
    return step ? momentOf(step) : null;
  }, [visit]);
  const taps = useMemo(() => (visit ? visitTaps(visit, visits[index + 1]) : []), [visit, visits, index]);
  const hints = useMemo(() => (latest.current ? screenHints(latest.current, visits, Math.min(index, Math.max(0, visits.length - 1))) : undefined), [visits, index]);

  // The fingertip reports each press once it is over.
  const pressed = useCallback((serial) => {
    const resolve = pending.current.get(serial);
    if (!resolve) return;
    pending.current.delete(serial);
    resolve();
  }, []);

  // Each visit plays its presses one after the other, each once the one before
  // is over; shows what one of them did (a message sent, a habit ticked) right
  // after it; and leaves the page only once the press that left it is over.
  useEffect(() => {
    if (!playing || !visit) return undefined;
    let live = true;
    const timers = [];
    const waiting = pending.current;
    const pause = (ms) => new Promise((resolve) => { timers.push(window.setTimeout(resolve, ms)); });
    const press = (key) => new Promise((resolve) => {
      presses.current += 1;
      const serial = presses.current;
      waiting.set(serial, resolve);
      timers.push(window.setTimeout(() => pressed(serial), PRESS_LIMIT_MS));
      setTap({ key, serial });
    });
    const next = visits[index + 1];
    const started = Date.now();
    const readTheRest = () => pause(Math.max(0, readingTime(visit, next, speed) - (Date.now() - started)));
    (async () => {
      await pause(SETTLE_MS);
      let shown = false;
      for (const entry of taps) {
        if (!live) return;
        if (entry.leaving) {
          if (moment !== null && !shown) {
            shown = true;
            setPhase(1);
            await pause(MOMENT_MS / speed);
          }
          await readTheRest();
          if (!live) return;
        }
        await press(entry.key);
        if (!live) return;
        if (moment !== null && !shown && !entry.leaving && entry.moment === moment) {
          shown = true;
          setPhase(1);
        }
        await pause((entry.leaving ? LEAVE_MS : BETWEEN_MS) / speed);
      }
      if (!live) return;
      if (moment !== null && !shown) {
        setPhase(1);
        await pause(MOMENT_MS / speed);
      }
      await readTheRest();
      if (!live) return;
      if (index + 1 >= visits.length) {
        setPlaying(false);
        return;
      }
      setIndex(index + 1);
      setPhase(0);
    })();
    return () => {
      live = false;
      timers.forEach((timer) => window.clearTimeout(timer));
      for (const resolve of waiting.values()) resolve();
      waiting.clear();
    };
  }, [playing, visit, visits, index, speed, moment, taps, pressed]);

  // Keeps the screen playing in view inside the list of screens, never by moving the page.
  useEffect(() => {
    const list = listRef.current;
    if (!list || list.scrollHeight <= list.clientHeight) return;
    const row = list.querySelector('.is-current');
    if (!row) return;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (top < list.scrollTop) list.scrollTo({ top: Math.max(0, top - 8), behavior: 'smooth' });
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTo({ top: bottom - list.clientHeight + 8, behavior: 'smooth' });
  }, [index]);

  const grouped = useMemo(() => {
    const groups = [];
    for (const entry of sessions) {
      const heading = dayHeading(entry.start, now);
      if (groups.length === 0 || groups[groups.length - 1].heading !== heading) groups.push({ heading, sessions: [] });
      groups[groups.length - 1].sessions.push(entry);
    }
    return groups;
  }, [sessions, now]);

  if (!session) return <Empty title={tx('ui.mobile.no_sessions')} detail={tx('ui.mobile.no_sessions_detail')} />;
  const person = personOf(session);
  const finished = !playing && index >= visits.length - 1;

  return (
    <div className={`xo-mob-player${showPerson ? '' : ' is-single'}`}>
      <div className="xo-mob-session-list" role="listbox" aria-label={tx('ui.mobile.sessions_title')}>
        {grouped.map((group) => (
          <div key={group.heading} className="xo-mob-session-group">
            <h4>{group.heading}</h4>
            {group.sessions.map((entry) => (
              <SessionRow key={entry.id} session={entry} active={entry.id === session.id} onPick={pick} showPerson={showPerson} />
            ))}
          </div>
        ))}
      </div>

      <div className="xo-mob-stage">
        <header className="xo-mob-stage-head">
          <span className="xo-mob-avatar">{person.initials}</span>
          <span className="xo-mob-stage-who">
            {showPerson && session.accountUuid
              ? <Link to={`/xomics/mobile/accounts?account=${session.accountUuid}`}>{person.name}<ChevronRight aria-hidden="true" /></Link>
              : <strong>{person.name}</strong>}
            <small>
              <PlatformMark platform={session.platform} withLabel />
              {session.build && <span>{versionLabel(session.build)}</span>}
              {session.country && <span><Flag code={session.country} />{session.city || ''}</span>}
            </small>
          </span>
          <span className="xo-mob-stage-when">
            <strong>{localTime(session.start, session.zone, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong>
            <small>{tx('ui.mobile.session_length', { duration: lengthOf(session.end - session.start) })}</small>
          </span>
        </header>

        <div className="xo-mob-stage-body">
          <div className="xo-mob-stage-phone">
            <AppPhone platform={session.platform === 'android' ? 'android' : 'ios'} scale={0.62}
              clock={visit ? localTime(visit.start, session.zone, { hour: '2-digit', minute: '2-digit' }) : ''}>
              <AppScreen screen={visit ? visit.screen : 'launch'} part={visit ? visit.part : null} back={Boolean(visit && visit.back)}
                moment={phase === 1 ? moment : null} person={person} at={visit ? visit.start : session.start} as={as} hints={hints} />
              <TapLayer key={session.id} tap={tap} pace={speed} onDone={pressed} />
            </AppPhone>
            <div className="xo-mob-controls">
              <button type="button" className="xo-mob-play" onClick={() => {
                if (finished) { setIndex(0); setPhase(0); setPlaying(true); return; }
                setPlaying(!playing);
              }} aria-label={playing ? tx('ui.mobile.pause') : tx('ui.mobile.play')}>
                {finished ? <RotateCcw aria-hidden="true" /> : playing ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
              </button>
              <div className="xo-mob-scrub" role="group" aria-label={tx('ui.mobile.timeline')}>
                {visits.map((entry, position) => (
                  <button key={position} type="button" aria-label={screenName(entry, as)}
                    className={position < index ? 'is-past' : position === index ? 'is-now' : ''}
                    onClick={() => { setIndex(position); setPhase(0); }} />
                ))}
              </div>
              <div className="xo-mob-speed" role="group" aria-label={tx('ui.mobile.speed')}>
                {SPEEDS.map((value) => (
                  <button key={value} type="button" className={speed === value ? 'is-active' : ''} onClick={() => setSpeed(value)}>{value}×</button>
                ))}
              </div>
            </div>
          </div>

          <ol className="xo-mob-visits" ref={listRef}>
            {visits.map((entry, position) => {
              const next = visits[position + 1];
              const spent = (next ? next.start : entry.end) - entry.start;
              // Going back is never logged; the replay says where the person went back through.
              const actions = entry.back ? [{ label: tx('ui.mobile.went_back'), count: 1 }] : [];
              for (const step of entry.steps) {
                const label = stepLabel(step, as);
                if (label === null) continue;
                const found = actions.find((item) => item.label === label);
                if (found) found.count += 1; else actions.push({ label, count: 1 });
              }
              const state = position === index ? 'is-current' : position < index ? 'is-past' : '';
              return (
                <li key={position} className={`${state}${entry.returned ? ' is-returned' : ''}`}>
                  <button type="button" onClick={() => { setIndex(position); setPhase(0); }}>
                    <span className="xo-mob-visit-time">+{lengthOf(entry.start - session.start)}</span>
                    <span className="xo-mob-visit-icon"><ScreenIcon screen={entry.screen} /></span>
                    <span className="xo-mob-visit-main">
                      <strong>{screenName(entry, as)}</strong>
                      {actions.length > 0 && (
                        <span className="xo-mob-visit-actions">
                          {actions.map((action) => <em key={action.label}>{action.label}{action.count > 1 ? ` ×${action.count}` : ''}</em>)}
                        </span>
                      )}
                      {entry.reopened > 0 && <small>{counted('ui.mobile.reopened', entry.reopened)}</small>}
                    </span>
                    <span className="xo-mob-visit-spent">{spent > 0 ? lengthOf(spent) : ''}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
        <p className="xo-mob-footnote"><CircleUserRound aria-hidden="true" />{tx('ui.mobile.replay_note')}</p>
      </div>
    </div>
  );
}
